import {
    Component, ElementRef, EventEmitter, HostBinding,
    Input, OnChanges, OnInit, Output, Renderer2, SimpleChanges, ViewChild, NgModule
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CreateControlAccessProvider, WOption, WOptionOrString, propChanged } from '@wipo/w-angular/shared';
import { WInputWDropdownDirective } from '@wipo/w-angular/w-input-search';
import { FFacetInfoModule } from '@wipo/w-angular/internal';
import { WInputBadgeModule } from '@wipo/w-angular/w-input-badge';
import { WInputSearchModule } from '@wipo/w-angular/w-input-search';
import { WButtonModule } from '@wipo/w-angular/w-button';
import { WInputDropdownModule } from '@wipo/w-angular/w-input-dropdown';
import { WClickOutsideModule } from '@wipo/w-angular/directives';

@Component({
    selector: 'w-select-many',
    templateUrl: './w-select-many.component.html',
    providers: [CreateControlAccessProvider(WSelectManyComponent)],
    host: {
        class: 'b-input b-input-select-many b-input-dropdown'
    },
    standalone: false
})
/*
@id w-select-many
@name w-select-many
@status release
@description
*/
export class WSelectManyComponent extends WInputWDropdownDirective implements OnInit, OnChanges {
    @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
    @HostBinding('class') c = 'b-input';

    @Input() options?: (WOption | string)[]; // flat array of strings or WOption objects
    @Input() reset?: Observable<void>; // if present monitored to reset selection
    @Input() maxBadgeWidth: number = 140; // maximum width in px
    @Input() showSelectedLines: number = 3; // show no more than x lines of selected items

    @Output() wSelect = new EventEmitter<any>(); // emits on Selection
    @Output() wUnselect = new EventEmitter<any>(); // emits on Unselect
    @Output() wUpdated = new EventEmitter<(any)[]>(); // triggers on change, either select or unselect

    searchText?: string;
    searchTextShow: boolean = false;

    override dropdownSelected: WOptionOrString[] = [];

    focused: boolean = false;
    get selectedItems() {
        return this.value;
    }
    get maxHeight() {
        return this.showSelectedLines * 39 + 4;
    }
    // tslint:disable-next-line: variable-name
    constructor(protected _renderer: Renderer2, protected _elementRef: ElementRef) {
        super();
        this.dropdownShow = true;
    }
    override ngOnInit(): void {
        super.ngOnInit();
        this.wDropdownClick.pipe(takeUntil(this.destruction)).subscribe(this.addSelected.bind(this));
        this.wKeyup.pipe(takeUntil(this.destruction)).subscribe(this.filter.bind(this));
        if (this.reset) {
            this.reset.pipe(takeUntil(this.destruction)).subscribe(this.resetAll.bind(this));
        }
        this.registerOnChange(this.updateSelection.bind(this));
    }
    updateSelection() {
        if (Array.isArray(this.value) && Array.isArray(this.options)) {
            this.dropdownSelected.splice(0, this.dropdownSelected.length);
            this.options.filter(o => {
                const v = typeof o === 'string' ? o : o.value;
                return this.value.indexOf(v) > -1;
            }).forEach(o => {
                this.dropdownSelected.push(o);
            });
        }
    }
    ngOnChanges(changes: SimpleChanges): void {
        if (propChanged(changes, 'options')) {
            // update options
            this.updateOptions();
        }
    }
    updateOptions() {
        this.dropdownList = [...(this.options || [])];
        this.updateSelection();
    }
    cleanupOnExit() {
        this.focused = false;
        this.clear();
    }
    override clearText(): string {
        return this.searchText = '';
    }
    clickOutside() {
        this.cleanupOnExit();
    }
    clickInside(e: MouseEvent, buttonWrapper: HTMLElement) {
        if (!this.disabled) {
            const fromButton = buttonWrapper && document.activeElement && buttonWrapper.contains(document.activeElement);
            this.searchTextShow = !fromButton; // show search field if we did not click on dropdown button
            this.focused = true;
            if (!fromButton) { this.inputFocus(e); }
        }
    }
    dropDown(event: MouseEvent) {
        if (Array.isArray(this.options) && (!Array.isArray(this.dropdownList) || this.options.length !== this.dropdownList.length)) {
            this.updateOptions();
        }
        this.clickSearch();
    }
    override inputFocus(event: FocusEvent) {
        super.inputFocus(event);
        setTimeout(() => {
            this.focus();
        });
    }

    override hasText(): boolean {
        return !!this.searchText;
    }
    override getText(): string {
        return this.searchText || '';
    }
    filter() {
        const test = (this.searchText || '').toLowerCase();
        this.dropdownList = this.options?.filter((o: WOptionOrString) => {
            if (typeof o === 'string') {
                return o.toLowerCase().indexOf(test) > -1;
            } else if (o?.label) {
                return o.label.toLowerCase().indexOf(test) > -1;
            } else {
                return false;
            }
        }) || [];
    }
    blurHandler(event: FocusEvent): void {
        this.searchText = '';
        this.focused = false;
        this.blur();
    }
    getItemLabel(item: WOptionOrString) {
        return typeof item === 'string' ? item : item.label;
    }
    getItemValue(item: WOptionOrString) {
        return typeof item === 'string' ? item : item.value;
    }
    addSelected(item: WOptionOrString) {
        const canAdd = typeof item === 'string' || !item.disabled;
        if (canAdd && this.dropdownSelected.indexOf(item) === -1) {
            this.dropdownSelected.push(item);
            if (Array.isArray(this.value)) {
                this.value.push(this.getItemValue(item));
            } else { this.value = [this.getItemValue(item)]; }
            this.wSelect.emit(item);
            this.wUpdated.emit(this.value);
        }
        this.clear();
    }
    removeItem(item: WOptionOrString) {
        const idx = this.dropdownSelected.indexOf(item);
        if (idx > -1) {
            this.value.splice(idx, 1);
            this.dropdownSelected.splice(idx, 1);
            this.wUnselect.emit(item);
            this.wUpdated.emit(this.value);
        }
    }
    resetAll() {
        if (Array.isArray(this.value)) { this.value.splice(0, this.value.length); }
        this.filter();
        this.clear();
        this.dropdownSelected.splice(0, this.dropdownSelected.length);
        this.wUpdated.emit(this.value);
    }
}
@NgModule({
    imports: [CommonModule, FormsModule, FFacetInfoModule, WClickOutsideModule,
        WInputSearchModule, WInputBadgeModule, WButtonModule, WInputDropdownModule],
    exports: [WSelectManyComponent],
    declarations: [WSelectManyComponent]
})
export class WSelectManyModule { }
